import { Divider, Grid } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "../../../../../../shared/components/DataLoadingFailed";
import ScrollableFlexContainer from "../../../../../../shared/components/ScrollableFlexContainer";
import InlineLoader from "../../../../../../shared/components/inlineLoader/InlineLoader";
import useFetch from "../../../../../../shared/hooks/useFetch";
import useDeferredDictionaryLoading from "../../../../../../shared/reporting/components/viewer/common/hooks/useDeferredDictionaryLoading";
import { useBiCommonDataContext } from "../../../../../../shared/reporting/contexts/BiCommonDataContext";
import biClient from "../../../../../api/biApi";
import { DrillDownInfo, MeasureDrillDownInfo } from "../../../../../api/biApi.types";
import useDrillDown from "../../../../../hooks/useDrillDown";
import { selectSessionId } from "../../../../../store/selectors/currentReportSelectors";
import { selectMeasures, selectMeasuresStructure } from "../../../../../store/selectors/metaDataSelectors";
import { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import SplitPane from "../../../../common/split-pane/SplitPane";
import MeasureSelector from "../../chartOfAccounts/MeasureSelector";
import { ChartOfAccountsContextProvider } from "../../chartOfAccounts/contexts/ChartOfAccountsContextProvider";
import { Properties } from "../../chartOfAccounts/properties/Properties";
import FormulaContainer from "../../customMeasure/FormulaContainer";
import { useCustomMeasure } from "../../customMeasure/hooks/useCustomMeasure";
import useCommonReportHandlers from "../../hooks/useCommonReportHandlers";
import ReadonlyConditions from "../ReadonlyConditions";
import LedgerAccountsWrapper from "./LedgerAccountsWrapper";

interface Props {
  configuration: DrillDownPage;
  info: DrillDownInfo;
}

export default function ChartOfAccountsPart({ configuration, info }: Props) {
  const storedMeasures = useSelector(selectMeasures);
  const measuresStructure = useSelector(selectMeasuresStructure);
  const sessionId = useSelector(selectSessionId);
  const handlers = useCommonReportHandlers();

  const selectedMeasure = useMemo(() => {
    return storedMeasures.find((m) => m.name === configuration.measure?.meta.name);
  }, [storedMeasures, configuration.measure?.meta.name]);
  const { dataSetId } = useBiCommonDataContext();
  const drillDownState = useDrillDown(info.combined as MeasureDrillDownInfo);
  const customMeasureState = useCustomMeasure({ measure: selectedMeasure });

  const fetchChartOfAccounts = useCallback(
    () => biClient.getChartOfAccountsDrillDown(sessionId, configuration.cell.drillDownId || ""),
    [configuration.cell.drillDownId, sessionId]
  );
  const [chartOfAccountsResp, error, { isFetching }] = useFetch(fetchChartOfAccounts);

  useDeferredDictionaryLoading(
    dataSetId,
    {
      values: drillDownState.conditions,
      updateItem: drillDownState.actions.updateCondition,
    },
    handlers.onLoadDictionaries
  );

  if (!selectedMeasure) {
    return null;
  }

  if (error) {
    return <DataLoadingFailed title="Failed load Chart Of Accounts"></DataLoadingFailed>;
  }

  if (isFetching) {
    return <InlineLoader />;
  }

  return (
    <Grid container sx={{ flex: 1, flexWrap: "nowrap", width: "100%" }}>
      <ChartOfAccountsContextProvider>
        <SplitPane initialPrimarySize="30%" minPrimarySize="350px" minSecondarySize="350px">
          <Grid
            container
            sx={{ p: "1.3rem", pl: 3, gap: 2, flexDirection: "column", flexWrap: "nowrap", width: "100%" }}
          >
            <ReadonlyConditions state={drillDownState} configuration={configuration} info={info.combined} />
            <Grid container sx={{ flex: 1, flexWrap: "nowrap", width: "100%" }}>
              <Grid container sx={{ flex: 1, flexDirection: "column", gap: 2, width: "100%" }}>
                <MeasureSelector
                  measures={storedMeasures}
                  measuresStructure={measuresStructure}
                  measure={selectedMeasure}
                />
                <FormulaContainer state={customMeasureState} measure={selectedMeasure} editable={false} hideCheck />
                <Divider sx={{ mx: -3 }} />
                <ScrollableFlexContainer sx={{ "& .MuiBox-root": { overflowX: "hidden" } }}>
                  <Properties state={customMeasureState} />
                </ScrollableFlexContainer>
              </Grid>
            </Grid>
          </Grid>
          <LedgerAccountsWrapper
            configuration={configuration}
            info={info}
            chartOfAccounts={chartOfAccountsResp?.items || []}
          />
        </SplitPane>
      </ChartOfAccountsContextProvider>
    </Grid>
  );
}
